import {
  MAIL_ARCHIVE_TYPE,
  type ArchiveSearchResult,
  type CompanyCardDto,
  type EquipmentDto,
  type EquipmentState,
  type MailboxDto,
  type Person,
  type RequiredDocumentDto,
  type RequiredDocumentsState,
  type Role,
  type Site,
  type SiteRouting,
  type SiteRoutingSuggestion,
} from '@bk/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, Empty, ErrorText, Field, PageTitle, SectionLoader, Select } from '../../ui';
import { useTenantLogo } from '../../lib/brand';
import { SiteBrandEditor } from '../SiteBrand';
import { AddressInput, addressLine } from './AddressInput';
import { useSites } from './SiteScope';
import { SiteSlide } from './impresa/ActivitiesStep';
import { Photo, SiteDetails, titleOf, USE_ICON, useSitePhoto, where } from './impresa/SitesStep';

const SOURCE: Record<Site['source'], string> = {
  visura: 'dalla visura',
  persona: 'indicata da te',
  documento: 'da un documento',
  posta: 'dalla posta',
  sito: 'dal sito',
  registro: 'da una pagina pubblica con la P.IVA',
  online: 'da una pagina online sulla sede',
  supposto: 'supposto dal cervello',
};

/** La sede c'è stata ma non c'è più (ADR 0141): «fino al» è passato. */
export const ceased = (s: Pick<Site, 'closed_on'>) => !!s.closed_on && s.closed_on < new Date().toISOString().slice(0, 10);

/** Data AAAA-MM-GG come «15/06/2020». */
const day = (d: string) => d.split('-').reverse().join('/');

/** Il periodo della sede in parole (ADR 0141); null se non è noto. */
export function periodOf(s: Pick<Site, 'opened_on' | 'closed_on'>): string | null {
  if (!s.opened_on && !s.closed_on) return null;
  return [s.opened_on ? `dal ${day(s.opened_on)}` : null, s.closed_on ? `fino al ${day(s.closed_on)}` : null].filter(Boolean).join(' ');
}

/**
 * Sedi dell'azienda (ADR 0017, 0123, 0141): in alto le sedi certe (dalla visura o confermate), le cessate in fondo; sotto
 * le proposte, da confermare una per una. Le tolte non si mostrano. Il clic apre la scheda della sede.
 */
export function SitesPage() {
  const { isManager } = useTenant();
  const { data, isLoading } = useSites();
  const [adding, setAdding] = useState(false);
  const all = data?.data ?? [];
  // Prima la sede legale, poi le altre; le cessate in fondo.
  const certain = all.filter((s) => s.status === 'attiva').sort((x, y) => Number(ceased(x)) - Number(ceased(y)) || Number(y.legal) - Number(x.legal));
  const proposals = all.filter((s) => s.status === 'da_confermare');
  return (
    <>
      <PageTitle
        title="Sedi"
        subtitle="Dove lavora l’azienda. Entra in una sede per le sue attività economiche, le persone, gli impianti, i documenti e la posta."
        actions={
          isManager && (
            <Button onClick={() => setAdding(!adding)}>
              {adding ? 'Chiudi' : 'Aggiungi una sede'}
            </Button>
          )
        }
      />
      {adding && <SiteForm onDone={() => setAdding(false)} />}
      {isLoading && <SectionLoader />}
      {!isLoading && !certain.length && (
        <Empty>
          Nessuna sede certa: le sedi certe vengono dalla visura o le confermi tu tra le proposte, oppure
          aggiungile qui.
        </Empty>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        {certain.map((s) => (
          <SiteTile key={s.id} site={s} />
        ))}
      </div>
      {!!proposals.length && (
        <section className="mt-8">
          <h2 className="flex items-center gap-2 text-base font-semibold">
            Proposte <Badge tone="amber">{proposals.length}</Badge>
          </h2>
          <p className="mb-3 text-sm text-ink/55">
            Luoghi trovati in documenti, posta o pagine web ma non nella visura. Finché non li confermi non sono sedi: niente
            documenti obbligatori e niente posta attribuita a loro.
          </p>
          <div className="grid gap-3 md:grid-cols-2">
            {proposals.map((s) => (
              <ProposalTile key={s.id} site={s} manager={isManager} />
            ))}
          </div>
        </section>
      )}
    </>
  );
}

/** Conferma o scarta una sede proposta (ADR 0141): «Non è nostra» la toglie e non torna (ADR 0094). */
function useChoice(id: string) {
  const { t } = useTenant();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ok: boolean) => api(t(`/sites/${id}`), { method: 'PATCH', body: ok ? { status: 'attiva' } : { status: 'chiusa', motivo: 'non_nostra' } }),
    onSuccess: () => void qc.invalidateQueries(),
  });
}

/** Una sede proposta: dove, da dove viene (con la citazione) e la scelta. */
function ProposalTile({ site, manager }: { site: Site; manager: boolean }) {
  const { tenant } = useTenant();
  const choice = useChoice(site.id);
  // Il riferimento è «fonte — citazione»: la citazione mostra dove l'ha trovata.
  const [from, quote] = (site.source_ref ?? '').split(' — ');
  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4">
      <a href={`#/t/${tenant.id}/sedi/${site.id}`} className="font-semibold leading-snug hover:underline">
        {titleOf(site)}
      </a>
      <p className="text-sm text-ink/55">{where(site) ?? <span className="text-amber-700">Manca l’indirizzo</span>}</p>
      <p className="mt-1.5 text-xs text-ink/55">
        {SOURCE[site.source]}
        {from && `: ${from}`}
      </p>
      {quote && <p className="mt-1 line-clamp-3 text-xs italic text-ink/50">«{quote}»</p>}
      {manager && (
        <div className="mt-3 flex flex-wrap gap-2">
          <Button disabled={choice.isPending} onClick={() => choice.mutate(true)}>
            Conferma
          </Button>
          <Button variant="ghost" disabled={choice.isPending} onClick={() => choice.mutate(false)}>
            Non è nostra
          </Button>
        </div>
      )}
      <ErrorText>{choice.error ? errorMessage(choice.error) : ''}</ErrorText>
    </div>
  );
}

/** Una sede nell'elenco: foto o icona, nome, dove, stato, attività e persone; porta alla scheda. */
function SiteTile({ site }: { site: Site }) {
  const { tenant } = useTenant();
  const photo = useSitePhoto(site);
  return (
    <a
      href={`#/t/${tenant.id}/sedi/${site.id}`}
      className={`flex items-start gap-3.5 rounded-2xl border border-black/5 bg-white p-4 transition hover:border-accent/40 hover:shadow-sm ${ceased(site) ? 'opacity-60' : ''}`}
    >
      {photo ? (
        <Photo photo={photo} alt={site.name} credit={false} className="h-16 w-16 shrink-0 rounded-xl" />
      ) : (
        <span aria-hidden className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-paper text-2xl">
          {site.icona ?? (site.use ? USE_ICON[site.use] : '📍')}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className="font-semibold leading-snug">
          {titleOf(site)} <SiteBadges site={site} />
        </p>
        <p className="text-sm text-ink/55">{where(site) ?? <span className="text-amber-700">Manca l’indirizzo</span>}</p>
        {periodOf(site) && <p className="text-xs text-ink/50">Attiva {periodOf(site)}</p>}
        <p className="mt-1.5 flex flex-wrap gap-1">
          {site.activities.length ? (
            site.activities.map((a) => (
              <Badge key={a.id} tone="green">
                {a.name}
              </Badge>
            ))
          ) : (
            <span className="text-xs text-amber-700">Nessuna attività economica indicata</span>
          )}
        </p>
        <p className="mt-1.5 text-xs text-ink/50">
          {site.people === 1 ? '1 persona' : `${site.people} persone`}
          {site.employees !== null && ` · ${site.employees} addetti`} · {SOURCE[site.source]}
        </p>
      </div>
    </a>
  );
}

function SiteBadges({ site }: { site: Site }) {
  return (
    <>
      {site.legal && <Badge tone="blue">{ceased(site) && site.closed_on ? `sede legale fino al ${day(site.closed_on)}` : 'sede legale'}</Badge>}{' '}
      {site.status === 'da_confermare' && <Badge tone="amber">proposta</Badge>}
      {site.status === 'attiva' && ceased(site) && <Badge>cessata</Badge>}
      {site.status === 'chiusa' && <Badge>tolta</Badge>}
    </>
  );
}

/** Un riquadro della scheda della sede: titolo, numero, link «Tutti» e contenuto. */
function Section({ title, count, more, children }: { title: string; count?: number; more?: { href: string; label: string }; children: ReactNode }) {
  return (
    <Card>
      <div className="mb-2 flex items-baseline gap-2">
        <h2 className="flex-1 text-sm font-semibold">
          {title}
          {count !== undefined && <span className="ml-1.5 font-normal text-ink/45">{count}</span>}
        </h2>
        {more && (
          <a href={more.href} className="text-xs text-accent hover:underline">
            {more.label}
          </a>
        )}
      </div>
      {children}
    </Card>
  );
}

/**
 * Scheda della sede (ADR 0123): in testa la sede con conferma, modifica e togli; poi le attività economiche (si
 * confermano e se ne aggiungono), ciò che dicono visura e web, e tutto ciò che riguarda la sede: persone, impianti,
 * documenti obbligatori, posta e regole della posta.
 */
export function SitePage({ id }: { id: string }) {
  const { tenant, t, isManager } = useTenant();
  const companyLogo = useTenantLogo(tenant.id);
  const qc = useQueryClient();
  const { data, isLoading } = useSites();
  const company = useTenantQuery<CompanyCardDto>(['company'], '/company');
  const [editing, setEditing] = useState(false);
  const all = data?.data ?? [];
  const site = all.find((s) => s.id === id);
  const status = useMutation({
    mutationFn: (s: 'attiva' | 'chiusa') => api(t(`/sites/${id}`), { method: 'PATCH', body: { status: s } }),
    onSuccess: () => void qc.invalidateQueries(),
  });
  const choice = useChoice(id);
  const back = (
    <a href={`#/t/${tenant.id}/sedi`} className="mb-3 inline-block text-sm text-accent hover:underline">
      ← Sedi
    </a>
  );
  if (!site)
    return (
      <>
        {back}
        {isLoading ? <SectionLoader /> : <Empty>Questa sede non c’è più.</Empty>}
      </>
    );
  const inTown = all.filter((y) => !!site.municipality && y.municipality === site.municipality).length;
  const acts = (company.data?.attivita ?? []).filter((a) => a.stato !== 'cessata');
  return (
    <>
      {back}
      <PageTitle
        title={titleOf(site)}
        subtitle={where(site) ?? undefined}
        actions={
          isManager && (
            <div className="flex flex-wrap gap-2">
              {site.status === 'da_confermare' && (
                <>
                  <Button disabled={choice.isPending} onClick={() => choice.mutate(true)}>
                    Conferma
                  </Button>
                  <Button variant="ghost" disabled={choice.isPending} onClick={() => choice.mutate(false)}>
                    Non è nostra
                  </Button>
                </>
              )}
              <Button variant="ghost" onClick={() => setEditing(!editing)}>
                {editing ? 'Chiudi' : 'Modifica'}
              </Button>
              {site.status === 'da_confermare' ? null : site.status !== 'chiusa' ? (
                <Button variant="ghost" disabled={status.isPending} onClick={() => status.mutate('chiusa')} title="Chiusa o non dell’azienda: esce dall’elenco">
                  Togli
                </Button>
              ) : (
                <Button variant="ghost" disabled={status.isPending} onClick={() => status.mutate('attiva')}>
                  Riattiva
                </Button>
              )}
            </div>
          )
        }
      />
      <p className="-mt-3 mb-4">
        <SiteBadges site={site} />
        {periodOf(site) && <span className="ml-1 text-sm text-ink/55">Attiva {periodOf(site)}</span>}
      </p>
      {site.status === 'da_confermare' && (
        <p className="mb-4 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900">
          È una proposta: trovata {SOURCE[site.source]}, non nella visura. Confermala se è davvero una sede dell’azienda; finché
          non la confermi non ha documenti obbligatori né posta attribuita.
        </p>
      )}
      <ErrorText>{status.error ? errorMessage(status.error) : choice.error ? errorMessage(choice.error) : ''}</ErrorText>
      {editing && <SiteForm site={site} onDone={() => setEditing(false)} />}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-4">
          {company.data ? (
            <SiteSlide
              site={site}
              dir={0}
              acts={acts}
              ideal={null}
              manager={isManager}
              onBack={null}
              onNotOperational={null}
            />
          ) : (
            <SectionLoader />
          )}
          <Section title="Dalla visura e dal web">
            <SiteDetails s={site} inTown={inTown} manager={isManager} />
          </Section>
          {isManager && <SiteMail site={site} />}
          {isManager && <Routing site={site} />}
        </div>
        <div className="min-w-0 space-y-4">
          <Section title="Marchio della sede">
            <SiteBrandEditor tenantId={tenant.id} s={site} manager={isManager} companyLogo={companyLogo} />
          </Section>
          <SitePeople site={site} />
          <SiteEquipment site={site} />
          <SiteDocuments site={site} />
        </div>
      </div>
    </>
  );
}

/** Chi lavora nella sede: chi è legato a questa sede e chi lavora su tutte. */
function SitePeople({ site }: { site: Site }) {
  const { tenant } = useTenant();
  const people = useTenantQuery<{ data: Person[] }>(['people'], '/people');
  const roles = useTenantQuery<{ data: Role[] }>(['roles'], '/roles');
  const label = (k: string) => roles.data?.data.find((r) => r.key === k)?.label ?? k;
  const here = (people.data?.data ?? []).filter((p) => p.active && (p.all_sites || p.sites.some((x) => x.site_id === site.id)));
  return (
    <Section title="Persone" count={here.length} more={{ href: `#/t/${tenant.id}/persone`, label: 'Tutte' }}>
      {people.isLoading && <p className="text-sm text-ink/50">Carico…</p>}
      {people.data && !here.length && <p className="text-sm text-ink/50">Nessuna persona registrata in questa sede.</p>}
      <ul className="divide-y divide-black/5 text-sm">
        {here.map((p) => {
          const at = p.sites.find((x) => x.site_id === site.id);
          const r = at?.roles.length ? at.roles : p.roles;
          return (
            <li key={p.id} className="py-1.5">
              <p className="font-medium">
                {p.name} {p.all_sites && <span className="text-xs font-normal text-ink/45">· tutte le sedi</span>}
              </p>
              {!!r.length && <p className="text-xs text-ink/55">{r.map(label).join(', ')}</p>}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

const EQUIPMENT_STATUS: Record<EquipmentDto['stato'], { label: string; tone: 'gray' | 'amber' | 'green' }> = {
  presente: { label: 'presente', tone: 'green' },
  atteso: { label: 'da confermare', tone: 'amber' },
  da_verificare: { label: 'da verificare', tone: 'amber' },
  non_presente: { label: 'non c’è', tone: 'gray' },
};

/** Impianti e attrezzature della sede (ADR 0023), senza quelli che il titolare ha detto che non ci sono. */
function SiteEquipment({ site }: { site: Site }) {
  const state = useTenantQuery<EquipmentState>(['equipment'], '/company/equipment');
  const list = (state.data?.data ?? []).filter((x) => x.sede === site.id && x.stato !== 'non_presente');
  return (
    <Section title="Impianti e attrezzature" count={list.length}>
      {state.isLoading && <p className="text-sm text-ink/50">Carico…</p>}
      {state.data && !list.length && <p className="text-sm text-ink/50">Nessun impianto noto in questa sede.</p>}
      <ul className="divide-y divide-black/5 text-sm">
        {list.map((x) => (
          <li key={x.id} className="flex items-center gap-2 py-1.5">
            <span aria-hidden>{x.icona ?? '⚙️'}</span>
            <span className="min-w-0 flex-1 truncate" title={x.perche ?? undefined}>
              {x.nome}
              {x.quantita !== null && x.quantita > 1 && <span className="text-ink/50"> × {x.quantita}</span>}
            </span>
            <Badge tone={EQUIPMENT_STATUS[x.stato].tone}>{EQUIPMENT_STATUS[x.stato].label}</Badge>
          </li>
        ))}
      </ul>
    </Section>
  );
}

const DOC_STATUS: Partial<Record<RequiredDocumentDto['stato'], { label: string; tone: 'amber' | 'green' | 'red' }>> = {
  da_caricare: { label: 'da caricare', tone: 'red' },
  incompleto: { label: 'incompleto', tone: 'amber' },
  da_confermare: { label: 'da confermare', tone: 'amber' },
  caricato: { label: 'a posto', tone: 'green' },
  da_rinnovare: { label: 'da rinnovare', tone: 'red' },
};

/** Documenti obbligatori della sede (ADR 0017): quelli dell'impresa nel suo insieme stanno in Archivio. */
function SiteDocuments({ site }: { site: Site }) {
  const { tenant } = useTenant();
  const q = useTenantQuery<RequiredDocumentsState>(['archive', 'required'], '/archive/required');
  const list = (q.data?.data ?? []).filter((d) => d.ambito.ambito === 'sedi' && d.ambito.sedi.includes(site.id) && d.stato !== 'non_riguarda');
  const missing = list.filter((d) => d.mancanti > 0 || d.stato === 'da_caricare' || d.stato === 'da_rinnovare').length;
  return (
    <Section title="Documenti obbligatori" count={list.length} more={{ href: `#/t/${tenant.id}/archivio`, label: 'Archivio' }}>
      {q.isLoading && <p className="text-sm text-ink/50">Carico…</p>}
      {q.data && !list.length && <p className="text-sm text-ink/50">Nessun documento obbligatorio proprio di questa sede.</p>}
      {missing > 0 && <p className="mb-1 text-xs text-amber-700">{missing === 1 ? 'Ne manca 1' : `Ne mancano ${missing}`}: caricali in Archivio.</p>}
      <ul className="divide-y divide-black/5 text-sm">
        {list.map((d) => {
          const st = DOC_STATUS[d.stato];
          return (
            <li key={d.id} className="flex items-center gap-2 py-1.5">
              <span className="min-w-0 flex-1" title={d.perche}>
                {d.titolo}
                {d.mancanti > 1 && <span className="text-xs text-ink/50"> · {d.mancanti} mancanti</span>}
              </span>
              {st && <Badge tone={st.tone}>{st.label}</Badge>}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

const MAIL_PAGE = 10;

/** La posta attribuita a questa sede (non quella di tutta l'azienda), la più recente prima; al clic il testo. */
function SiteMail({ site }: { site: Site }) {
  const { tenant, t } = useTenant();
  const [offset, setOffset] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['tenant', tenant.id, 'site-mail', site.id, offset],
    queryFn: () =>
      api<ArchiveSearchResult>(t('/archive/search'), {
        body: { tipo: MAIL_ARCHIVE_TYPE, sede: site.id, solo_sede: true, limite: MAIL_PAGE, offset },
      }),
    placeholderData: keepPreviousData,
  });
  const rows = [...(q.data?.righe ?? [])];
  const text = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
  return (
    <Section title="Posta della sede" count={q.data?.totale}>
      <ErrorText>{q.error ? errorMessage(q.error) : ''}</ErrorText>
      {q.isLoading && <p className="text-sm text-ink/50">Carico…</p>}
      {q.data && !rows.length && (
        <p className="text-sm text-ink/50">
          Nessun messaggio attribuito a questa sede. Il sistema li attribuisce leggendo la posta; con le regole qui sotto
          li mandi qui a monte.
        </p>
      )}
      <ul className="divide-y divide-black/5 text-sm">
        {rows.map((r) => {
          const m = r.attributi;
          const shown = open === r.id;
          return (
            <li key={r.id} className="py-1.5">
              <button className="flex w-full items-baseline gap-3 text-left hover:text-accent" onClick={() => setOpen(shown ? null : r.id)}>
                <span className="w-20 shrink-0 text-xs text-ink/50">{text(m.data).slice(0, 10)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">{text(m.oggetto) || '(senza oggetto)'}</span>
                  <span className="block truncate text-xs text-ink/55">{text(m.da)}</span>
                </span>
              </button>
              {shown && (
                <p className="mt-1 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-lg bg-black/[0.03] p-3 text-xs text-ink/75">
                  {text(m.testo).slice(0, 4000) || 'Nessun testo.'}
                </p>
              )}
            </li>
          );
        })}
      </ul>
      {!!q.data && q.data.totale > MAIL_PAGE && (
        <div className="mt-2 flex items-center gap-2 text-xs text-ink/55">
          <span className="flex-1">
            {offset + 1}–{Math.min(offset + MAIL_PAGE, q.data.totale)} di {q.data.totale}
          </span>
          <Button variant="ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - MAIL_PAGE))}>
            Precedenti
          </Button>
          <Button variant="ghost" disabled={offset + MAIL_PAGE >= q.data.totale} onClick={() => setOffset(offset + MAIL_PAGE)}>
            Successivi
          </Button>
        </div>
      )}
    </Section>
  );
}

export function SiteForm({ site, onDone }: { site?: Site; onDone: () => void }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const [v, setV] = useState({
    name: site?.name ?? '',
    address: site?.address ?? '',
    municipality: site?.municipality ?? '',
    province: site?.province ?? '',
    activity: site?.activity ?? '',
    postal_code: site?.postal_code ?? '',
    region: site?.region ?? '',
    country: site?.country ?? '',
    employees:
      site?.employees === null || site?.employees === undefined ? '' : String(site.employees),
    legal: site?.legal ?? false,
    opened_on: site?.opened_on ?? '',
    closed_on: site?.closed_on ?? '',
  });
  // Indirizzo dai suggerimenti di Google; i campi a mano solo se il server non ha il servizio (ADR 0028).
  const [picked, setPicked] = useState(false);
  const [manual, setManual] = useState(false);
  const save = useMutation({
    mutationFn: () => {
      const parts = [
        'address',
        'municipality',
        'province',
        'postal_code',
        'region',
        'country',
      ] as const;
      // Un indirizzo scelto sostituisce tutti i pezzi (anche quelli che non ha); scritto a mano completa quelli dati.
      const where = Object.fromEntries(
        parts.flatMap((k): Array<[string, string | null]> =>
          v[k].trim() ? [[k, v[k].trim()]] : picked && site ? [[k, null]] : [],
        ),
      );
      const body = {
        name: v.name.trim(),
        ...where,
        ...(v.activity.trim() ? { activity: v.activity.trim() } : {}),
        ...(v.employees.trim() && Number.isInteger(Number(v.employees))
          ? { employees: Number(v.employees) }
          : {}),
        legal: v.legal,
        // Il periodo della sede (ADR 0141): una data tolta si cancella.
        ...(v.opened_on ? { opened_on: v.opened_on } : site?.opened_on ? { opened_on: null } : {}),
        ...(v.closed_on ? { closed_on: v.closed_on } : site?.closed_on ? { closed_on: null } : {}),
        ...(site?.status === 'da_confermare' ? { status: 'attiva' } : {}),
      };
      return site
        ? api(t(`/sites/${site.id}`), { method: 'PATCH', body })
        : api(t('/sites'), { body });
    },
    onSuccess: () => {
      void qc.invalidateQueries();
      onDone();
    },
  });
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) =>
    setV({ ...v, [k]: e.target.value });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <Card className="my-3">
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
        <Field label="Nome della sede" value={v.name} onChange={set('name')} required />
        {manual ? (
          <>
            <Field label="Indirizzo" value={v.address} onChange={set('address')} />
            <Field label="Comune" value={v.municipality} onChange={set('municipality')} />
            <Field label="Provincia" value={v.province} onChange={set('province')} />
            <Field label="CAP" value={v.postal_code} onChange={set('postal_code')} />
            <Field label="Regione" value={v.region} onChange={set('region')} />
          </>
        ) : (
          <div className="block text-sm sm:col-span-2">
            <span className="mb-1 block font-medium text-ink/80">Indirizzo</span>
            <AddressInput
              value={addressLine({
                address: v.address || null,
                postal_code: v.postal_code || null,
                municipality: v.municipality || null,
                province: v.province || null,
              })}
              onPick={(x) => {
                setPicked(true);
                setV({
                  ...v,
                  address: x.address ?? '',
                  postal_code: x.postal_code ?? '',
                  municipality: x.municipality ?? '',
                  province: x.province ?? '',
                  region: x.region ?? '',
                  country: x.country ?? '',
                });
              }}
              onUnavailable={() => setManual(true)}
            />
          </div>
        )}
        <Field
          label="Addetti in questa sede"
          type="number"
          min={0}
          value={v.employees}
          onChange={set('employees')}
        />
        <Field label="Attiva dal" type="date" value={v.opened_on} onChange={set('opened_on')} />
        <Field
          label="Fino al (se non c’è più)"
          type="date"
          min={v.opened_on || undefined}
          value={v.closed_on}
          onChange={set('closed_on')}
        />
        <label className="sm:col-span-2 block text-sm">
          <span className="mb-1 block font-medium text-ink/80">Cosa vi si svolge</span>
          <textarea
            className="w-full rounded-lg border border-black/15 px-3 py-2 text-sm"
            rows={2}
            value={v.activity}
            onChange={set('activity')}
          />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={v.legal}
            onChange={(e) => setV({ ...v, legal: e.target.checked })}
          />{' '}
          Sede legale
        </label>
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" disabled={save.isPending || !v.name.trim()}>
            Salva
          </Button>
          <Button type="button" variant="ghost" onClick={onDone}>
            Annulla
          </Button>
        </div>
      </form>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </Card>
  );
}

/**
 * Regole della posta di una sede: quelle che vi mandano la posta (e quelle per tutta l'azienda), le suggerite dal
 * sistema da accettare, e il modulo per aggiungerne una diretta alla sede.
 */
function Routing({ site }: { site: Site }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const rules = useTenantQuery<{ data: SiteRouting[] }>(['sites', 'routing'], '/sites/routing');
  const suggestions = useTenantQuery<{ data: SiteRoutingSuggestion[] }>(
    ['sites', 'routing', 'suggestions'],
    '/sites/routing/suggestions',
  );
  const boxes = useTenantQuery<{ data: MailboxDto[] }>(['mailboxes'], '/mailboxes');
  const [v, setV] = useState({ mailbox_id: '', folder: '', sender: '', recipient: '' });
  const refresh = () => void qc.invalidateQueries({ queryKey: ['tenant'] });
  const add = useMutation({
    mutationFn: () =>
      api(t('/sites/routing'), {
        body: {
          mailbox_id: v.mailbox_id || null,
          folder: v.folder.trim() || null,
          sender: v.sender.trim() || null,
          recipient: v.recipient.trim() || null,
          ambito: 'sedi',
          sedi: [site.id],
        },
      }),
    onSuccess: () => {
      setV({ mailbox_id: '', folder: '', sender: '', recipient: '' });
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(t(`/sites/routing/${id}`), { method: 'DELETE' }),
    onSuccess: refresh,
  });
  const accept = useMutation({
    mutationFn: (s: SiteRoutingSuggestion) =>
      api(t('/sites/routing/accept'), {
        body: { mailbox_id: s.mailbox_id, sender: s.sender, ambito: 'sedi', sedi: s.sedi },
      }),
    onSuccess: refresh,
  });
  const mine = (rules.data?.data ?? []).filter((r) => r.sedi.includes(site.id));
  const everywhere = (rules.data?.data ?? []).filter((r) => r.ambito === 'azienda');
  const proposed = (suggestions.data?.data ?? []).filter((s) => s.sedi.includes(site.id));
  const rule = (r: SiteRouting) => (
    <div key={r.id} className="flex flex-wrap items-center gap-2 py-2 text-sm">
      <span className="flex-1">
        {[
          r.mailbox && `casella ${r.mailbox}`,
          r.folder && `cartella ${r.folder}`,
          r.sender && `da ${r.sender}`,
          r.recipient && `a ${r.recipient}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        {r.ambito === 'azienda' && <span className="text-ink/50"> → tutta l’azienda</span>}
        {r.ambito === 'sedi' && r.sedi.length > 1 && <span className="text-ink/50"> → anche altre sedi</span>}
        {r.origin === 'proposta' && <span className="text-ink/40"> · suggerita</span>}
      </span>
      <Button variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(r.id)}>
        Togli
      </Button>
    </div>
  );
  return (
    <Section title="Regole della posta">
      <p className="mb-3 text-xs text-ink/55">
        Indica a monte quale posta va a questa sede (per casella, cartella, mittente o dominio, destinatario): i
        messaggi che corrispondono, e i loro allegati, vanno qui senza che il sistema lo debba dedurre.
      </p>
      {proposed.length > 0 && (
        <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm">
          <p className="mb-2 font-medium">Regole suggerite dal sistema</p>
          {proposed.map((s) => (
            <div key={`${s.mailbox_id}${s.sender}`} className="flex flex-wrap items-center gap-2 py-1">
              <span className="flex-1">
                Posta da <b>{s.sender}</b> nella casella {s.mailbox} ({s.messaggi} messaggi su {s.totale})
              </span>
              <Button variant="ghost" disabled={accept.isPending} onClick={() => accept.mutate(s)}>
                Accetta
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="divide-y divide-black/5">
        {mine.map(rule)}
        {everywhere.map(rule)}
        {rules.data && !mine.length && !everywhere.length && <p className="py-2 text-sm text-ink/50">Nessuna regola.</p>}
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2">
        <Select label="Casella" value={v.mailbox_id} onChange={(e) => setV({ ...v, mailbox_id: e.target.value })}>
          <option value="">tutte</option>
          {boxes.data?.data.map((b) => (
            <option key={b.id} value={b.id}>
              {b.address}
            </option>
          ))}
        </Select>
        <Field label="Cartella" value={v.folder} onChange={(e) => setV({ ...v, folder: e.target.value })} />
        <Field label="Mittente o @dominio" value={v.sender} onChange={(e) => setV({ ...v, sender: e.target.value })} />
        <Field label="Destinatario" value={v.recipient} onChange={(e) => setV({ ...v, recipient: e.target.value })} />
      </div>
      <Button
        className="mt-2"
        variant="ghost"
        disabled={add.isPending || (!v.mailbox_id && !v.sender.trim() && !v.recipient.trim())}
        onClick={() => add.mutate()}
      >
        Aggiungi regola
      </Button>
      <ErrorText>
        {add.error ? errorMessage(add.error) : remove.error ? errorMessage(remove.error) : accept.error ? errorMessage(accept.error) : ''}
      </ErrorText>
    </Section>
  );
}
